import React, { useState } from 'react'
import { LabraToolTipV2, VerticalDivider } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronUp,
  faLink,
  faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { CloudMarketplace } from '@labrav/react-components/lib/@types/types/forms'
import { getCloudLogo } from '../utils/cloudUtils'
import { useStyles } from './CardBox.styles'
import { copyToClipboard } from '@labrav/common'
import clsx from 'clsx'

export interface CardBoxProps {
  id: string
  isCollapsed: boolean
  copyLinkUrl?: string
  onToggleCollapse: () => void
  onCardClick?: (id: string) => void
  cloudType?: string
  title?: string
  content?: React.ReactNode
  headerContent?: React.ReactNode
  viewLink?: boolean
}

export const CardBox: React.FC<CardBoxProps> = ({
  id,
  isCollapsed,
  onToggleCollapse,
  onCardClick,
  copyLinkUrl,
  cloudType,
  title,
  content,
  headerContent,
  viewLink,
}) => {
  const { classes } = useStyles()
  const [copiedOfferLink, setCopiedOfferLink] = useState(false)

  const handleOfferClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (onCardClick && id) {
      onCardClick(id)
    } else {
      console.warn('Cannot navigate - missing:', {
        hasOnOfferClick: !!onCardClick,
        hasOfferId: !!id,
      })
    }
  }

  const copyOfferLink = async (e: React.MouseEvent) => {
    // e.stopPropagation()
    e.preventDefault()
    const link = copyLinkUrl || ''
    if (!link) {
      console.error('No offer link found')
      return
    }
    try {
      await copyToClipboard(link)
      setCopiedOfferLink(true)
      setTimeout(() => {
        setCopiedOfferLink(false)
      }, 2000)
    } catch (error) {
      console.error('Error copying URL to clipboard:', error)
    }
  }

  return (
    <div>
      <div className={classes.card} aria-live="polite">
        <div className={classes.cardHeader}>
          <div className={clsx(classes.cardTitleRow)}>
            <div
              onClick={handleOfferClick}
              className={clsx(classes.cardTitleRow, classes.clickable)}
            >
              <span className={classes.cardTitle}>{title}</span>
              {viewLink && (
                <button
                  className={classes.linkIcon}
                  aria-label="View offer details"
                  type="button"
                >
                  <FontAwesomeIcon
                    style={{ width: '16px', height: '16px' }}
                    icon={faUpRightFromSquare}
                  />
                </button>
              )}
            </div>
            {copyLinkUrl && (
              <>
                <VerticalDivider height="24px" />
                <LabraToolTipV2
                  description={copiedOfferLink ? 'Copied' : 'Copy offer link'}
                  tooltipPlacement="top"
                  icon={<FontAwesomeIcon icon={faCheck} />}
                  className={classes.copyLinkIconTooltip}
                >
                  <div
                    onClick={copyOfferLink}
                    className={classes.copyLinkIcon}
                    aria-label="Copy offer link"
                  >
                    <FontAwesomeIcon
                      style={{ width: '20px', height: '20px' }}
                      icon={faLink}
                    />
                  </div>
                </LabraToolTipV2>
              </>
            )}
          </div>
          <div className={classes.actions}>
            <img
              src={getCloudLogo(cloudType as CloudMarketplace)}
              alt={cloudType}
              className={clsx(
                classes.cloudLogo,
                (cloudType?.toUpperCase() === 'AZURE' ||
                  cloudType?.toUpperCase() === 'MICROSOFT') &&
                  classes.cloudLogoMicrosoft
              )}
            />
            <VerticalDivider height="24px" />
            <button
              className={classes.chevronButton}
              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
              aria-expanded={!isCollapsed}
              onClick={e => {
                e.stopPropagation()
                onToggleCollapse()
              }}
            >
              <FontAwesomeIcon
                icon={faChevronUp}
                className={
                  isCollapsed
                    ? classes.chevronIconCollapsed
                    : classes.chevronIcon
                }
              />
            </button>
          </div>
        </div>
        {headerContent}
        {!isCollapsed && (
          <>
            <div className={classes.divider} />
            {content}
          </>
        )}
      </div>
    </div>
  )
}
